import { useEffect, useId, useRef, useState } from 'react'
import { ChevronDown } from 'lucide-react'
import type { RecordedFile } from '@/types'
import { formatKstDateTime } from '@/utils/datetime'
import Pagination from './Pagination'

const DEFAULT_PAGE_SIZE = 10

interface Props {
  files: RecordedFile[]
  title?: string
  description?: string
  emptyText?: string
  showCamera?: boolean
  showVideoMeta?: boolean
  groupByCamera?: boolean
  cameraOptions?: Array<{ key: string; label?: string | null }>
  activeCameraKey?: string | null
  onCameraChange?: (cameraKey: string) => void
  showCameraCounts?: boolean
  pageSize?: number
  onUploadSelected?: (fileIds: string[]) => void
  uploadDisabled?: boolean
  onSelectionChange?: (fileIds: string[]) => void
  onDeleteLocal?: (file: RecordedFile) => void
  deleteLocalDisabled?: boolean
  deletingFileId?: string | null
  showDeletedAt?: boolean
  defaultSortKey?: 'recorded_at' | 'deleted_at'
  collapsible?: boolean
  expanded?: boolean
  defaultExpanded?: boolean
  onExpandedChange?: (expanded: boolean) => void
  serverPagination?: {
    page: number
    total: number
    onPageChange: (page: number) => void
  }
}

const UNASSIGNED_CAMERA_KEY = '__unassigned__'

const UPLOAD_STATUS_STYLE: Record<string, { bg: string; text: string }> = {
  pending:   { bg: '#f5f5f5', text: '#555' },
  uploading: { bg: '#e3f2fd', text: '#1565c0' },
  uploaded:  { bg: '#e8f5e9', text: '#2e7d32' },
  failed:    { bg: '#ffebee', text: '#c62828' },
}

type FlagTone = 'success' | 'warning' | 'danger' | 'muted'

interface FileFlag {
  key: string
  label: string
  tone: FlagTone
  title?: string
  copyText?: string
}

const FLAG_STATUS_STYLE: Record<FlagTone, { bg: string; text: string; border: string }> = {
  success: { bg: '#edf7ed', text: '#2e7d32', border: '#c8e6c9' },
  warning: { bg: '#fff8e1', text: '#8a5a00', border: '#ffe0a3' },
  danger:  { bg: '#ffebee', text: '#c62828', border: '#ffcdd2' },
  muted:   { bg: '#f5f5f5', text: '#666', border: '#e0e0e0' },
}

function UploadBadge({ status }: { status: string }) {
  const s = UPLOAD_STATUS_STYLE[status] ?? { bg: '#f0f0f0', text: '#666' }
  return (
    <span
      style={{
        display: 'inline-block',
        padding: '2px 8px',
        borderRadius: 10,
        fontSize: 11,
        fontWeight: 600,
        background: s.bg,
        color: s.text,
        whiteSpace: 'nowrap',
      }}
    >
      {status}
    </span>
  )
}

function formatBytes(bytes: number | null): string {
  if (bytes == null) return '—'
  const gb = bytes / 1073741824
  if (gb >= 1) return `${gb.toFixed(2)} GB`
  const mb = bytes / 1048576
  return `${mb.toFixed(0)} MB`
}

function formatTime(ts: string | null): string {
  return formatKstDateTime(ts, '—')
}

function formatDuration(seconds: number | null): string {
  if (seconds == null) return '—'
  const total = Math.round(seconds)
  const hour = Math.floor(total / 3600)
  const min = Math.floor((total % 3600) / 60)
  const sec = total % 60
  if (hour > 0) return `${hour}h ${String(min).padStart(2, '0')}m ${String(sec).padStart(2, '0')}s`
  if (min === 0) return `${sec}s`
  return `${min}m ${String(sec).padStart(2, '0')}s`
}

function formatFps(fps: number | null): string {
  if (fps == null) return '—'
  return Number.isInteger(fps) ? String(fps) : fps.toFixed(2).replace(/\.?0+$/, '')
}

function formatResolution(file: RecordedFile): string {
  if (file.width == null || file.height == null) return '—'
  return `${file.width}x${file.height}`
}

function formatVideoMeta(file: RecordedFile): string {
  const parts: string[] = []
  if (file.duration_seconds != null) parts.push(formatDuration(file.duration_seconds))
  if (file.fps != null) parts.push(`${formatFps(file.fps)} fps`)
  if (file.width != null && file.height != null) parts.push(formatResolution(file))
  return parts.length ? parts.join(' | ') : '—'
}

function formatVideoHoverTitle(file: RecordedFile): string {
  return [
    `Video: ${formatVideoMeta(file)}`,
    `Codec: ${file.codec ?? '—'}`,
  ].join('\n')
}

function formatRowHoverTitle(file: RecordedFile): string {
  return [
    `File: ${file.file_name ?? '—'}`,
    `Local path: ${file.local_path ?? '—'}`,
    `Camera: ${file.camera_key ?? '—'}`,
    `Size: ${formatBytes(file.size_bytes)}`,
    `Recorded at: ${formatTime(file.recorded_at)}`,
    `Video: ${formatVideoMeta(file)}`,
    `Codec: ${file.codec ?? '—'}`,
    `Upload: ${file.upload_status}`,
    `Uploaded at: ${formatTime(file.uploaded_at)}`,
    `Delete: ${file.delete_status}`,
    `Deleted at: ${formatTime(file.deleted_at)}`,
    `Delete origin: ${file.delete_origin ?? '—'}`,
    `NAS path: ${file.nas_path ?? '—'}`,
    `Failure: ${file.delete_failure_reason ?? file.failure_reason ?? '—'}`,
  ].join('\n')
}

function deleteOriginLabel(origin: string | null): string {
  if (origin === 'upload_clean') return 'Uploaded & deleted'
  if (origin === 'operator') return 'Deleted by operator'
  if (origin === 'scan_reconcile') return 'Detected missing'
  return 'Deleted'
}

function buildFileFlags(file: RecordedFile): FileFlag[] {
  const flags: FileFlag[] = []
  const deleteFailureText = file.delete_failure_reason ?? file.failure_reason ?? undefined
  const uploadFailureText = file.failure_reason ?? undefined

  if (file.delete_status === 'delete_failed') {
    flags.push({
      key: `delete_failed:${file.id}`,
      label: 'Delete failed',
      tone: 'danger',
      title: deleteFailureText ? `Delete failed: ${deleteFailureText}` : 'Delete failed',
      copyText: deleteFailureText,
    })
  } else if (file.delete_status === 'deleted') {
    const label = deleteOriginLabel(file.delete_origin)
    flags.push({ key: `deleted:${file.id}`, label, tone: 'success', title: label })
  } else if (file.delete_status === 'deleting') {
    flags.push({ key: `deleting:${file.id}`, label: 'Deleting', tone: 'warning' })
  }

  if (file.upload_status === 'failed') {
    flags.push({
      key: `upload_failed:${file.id}`,
      label: 'Upload failed',
      tone: 'danger',
      title: uploadFailureText ? `Upload failed: ${uploadFailureText}` : 'Upload failed',
      copyText: uploadFailureText,
    })
  }

  if (file.possibly_truncated) {
    flags.push({ key: `truncated:${file.id}`, label: 'Truncated', tone: 'warning' })
  }

  return flags
}

async function copyToClipboard(text: string): Promise<void> {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text)
    return
  }

  const textarea = document.createElement('textarea')
  textarea.value = text
  textarea.style.position = 'fixed'
  textarea.style.left = '-9999px'
  document.body.appendChild(textarea)
  textarea.focus()
  textarea.select()
  document.execCommand('copy')
  document.body.removeChild(textarea)
}

function FlagBadge({
  flag,
  copiedKey,
  onCopy,
}: {
  flag: FileFlag
  copiedKey: string | null
  onCopy: (value: string, key: string) => void
}) {
  const s = FLAG_STATUS_STYLE[flag.tone]
  const style = {
    ...flagBadgeStyle,
    background: s.bg,
    color: s.text,
    borderColor: s.border,
    cursor: flag.copyText ? 'copy' : 'default',
  }
  const label = copiedKey === flag.key ? 'Copied' : flag.label

  if (!flag.copyText) {
    return (
      <span title={flag.title ?? flag.label} style={style}>
        {label}
      </span>
    )
  }

  return (
    <button
      type="button"
      title={flag.title ?? flag.label}
      onClick={(event) => {
        event.stopPropagation()
        onCopy(flag.copyText as string, flag.key)
      }}
      style={style}
    >
      {label}
    </button>
  )
}

function cameraFilterKey(file: RecordedFile): string {
  return file.camera_key ?? UNASSIGNED_CAMERA_KEY
}

function cameraLabel(key: string, options: Array<{ key: string; label?: string | null }>): string {
  if (key === UNASSIGNED_CAMERA_KEY) return 'Unassigned'
  const option = options.find((item) => item.key === key)
  const label = option?.label?.trim()
  return label && label !== key ? `${label} (${key})` : key
}

type SortKey = 'recorded_at' | 'deleted_at' | 'size_bytes' | 'upload_status' | 'file_name' | 'camera_key'
type SortDir = 'asc' | 'desc'

export default function FileList({
  files,
  title = 'Recorded Files',
  description,
  emptyText = 'No files found',
  showCamera = false,
  showVideoMeta = false,
  groupByCamera = false,
  cameraOptions = [],
  activeCameraKey: controlledActiveCameraKey,
  onCameraChange,
  showCameraCounts = true,
  pageSize = DEFAULT_PAGE_SIZE,
  onUploadSelected,
  uploadDisabled = false,
  onSelectionChange,
  onDeleteLocal,
  deleteLocalDisabled = false,
  deletingFileId = null,
  showDeletedAt = false,
  defaultSortKey = 'recorded_at',
  collapsible = false,
  expanded: controlledExpanded,
  defaultExpanded = true,
  onExpandedChange,
  serverPagination,
}: Props) {
  const [sortKey, setSortKey] = useState<SortKey>(defaultSortKey)
  const [sortDir, setSortDir] = useState<SortDir>('desc')
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [page, setPage] = useState(1)
  const [internalActiveCameraKey, setInternalActiveCameraKey] = useState<string | null>(null)
  const [internalExpanded, setInternalExpanded] = useState(defaultExpanded)
  const [copiedKey, setCopiedKey] = useState<string | null>(null)
  const [fromDate, setFromDate] = useState('')
  const [toDate, setToDate] = useState('')
  const onSelectionChangeRef = useRef(onSelectionChange)
  const contentId = useId()
  const effectivePageSize = Number.isFinite(pageSize) ? Math.max(1, Math.floor(pageSize)) : DEFAULT_PAGE_SIZE
  const expanded = collapsible ? controlledExpanded ?? internalExpanded : true

  useEffect(() => {
    onSelectionChangeRef.current = onSelectionChange
  }, [onSelectionChange])

  useEffect(() => {
    onSelectionChangeRef.current?.(Array.from(selected))
  }, [selected])

  async function handleCopy(value: string, key: string) {
    try {
      await copyToClipboard(value)
      setCopiedKey(key)
      window.setTimeout(() => setCopiedKey((current) => current === key ? null : current), 1400)
    } catch {
      setCopiedKey(null)
    }
  }

  function matchesDateRange(file: RecordedFile): boolean {
    if (!fromDate && !toDate) return true
    if (!file.recorded_at) return false
    const recordedAt = new Date(file.recorded_at).getTime()
    if (Number.isNaN(recordedAt)) return false
    if (fromDate) {
      const from = new Date(`${fromDate}T00:00:00`).getTime()
      if (!Number.isNaN(from) && recordedAt < from) return false
    }
    if (toDate) {
      const to = new Date(`${toDate}T23:59:59.999`).getTime()
      if (!Number.isNaN(to) && recordedAt > to) return false
    }
    return true
  }

  const filteredFiles = files.filter(matchesDateRange)
  const cameraCounts = new Map<string, number>()
  filteredFiles.forEach((file) => {
    const key = cameraFilterKey(file)
    cameraCounts.set(key, (cameraCounts.get(key) ?? 0) + 1)
  })
  const configuredCameraKeys = new Set<string>()
  const cameraTabs = cameraOptions
    .filter((camera) => {
      const key = camera.key.trim()
      if (!key || configuredCameraKeys.has(key)) return false
      configuredCameraKeys.add(key)
      return true
    })
    .map((camera) => ({
      key: camera.key,
      label: cameraLabel(camera.key, cameraOptions),
      count: cameraCounts.get(camera.key) ?? 0,
    }))
  const useCameraTabs = groupByCamera && cameraTabs.length > 1
  const cameraTabKeys = cameraTabs.map((tab) => tab.key).join('\u001f')
  const activeCameraKey = controlledActiveCameraKey === undefined
    ? internalActiveCameraKey
    : controlledActiveCameraKey
  const effectiveCameraKey = useCameraTabs ? activeCameraKey ?? cameraTabs[0]?.key ?? null : null
  const viewFiles = effectiveCameraKey
    ? filteredFiles.filter((file) => cameraFilterKey(file) === effectiveCameraKey)
    : filteredFiles

  function toggleSort(key: SortKey) {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortKey(key)
      setSortDir('desc')
    }
    setPage(1)
  }

  const sorted = serverPagination ? viewFiles : [...viewFiles].sort((a, b) => {
    let av: string | number | null = null
    let bv: string | number | null = null
    if (sortKey === 'recorded_at') {
      av = a.recorded_at
      bv = b.recorded_at
    } else if (sortKey === 'deleted_at') {
      av = a.deleted_at
      bv = b.deleted_at
    } else if (sortKey === 'size_bytes') {
      av = a.size_bytes
      bv = b.size_bytes
    } else if (sortKey === 'upload_status') {
      av = a.upload_status
      bv = b.upload_status
    } else if (sortKey === 'file_name') {
      av = a.file_name
      bv = b.file_name
    } else if (sortKey === 'camera_key') {
      av = a.camera_key
      bv = b.camera_key
    }
    if (av == null && bv == null) return 0
    if (av == null) return 1
    if (bv == null) return -1
    const cmp = av < bv ? -1 : av > bv ? 1 : 0
    return sortDir === 'asc' ? cmp : -cmp
  })
  const paginationTotal = serverPagination?.total ?? sorted.length
  const serverPage = serverPagination?.page
  const onServerPageChange = serverPagination?.onPageChange
  const totalPages = Math.max(1, Math.ceil(paginationTotal / effectivePageSize))
  const requestedPage = serverPage ?? page
  const effectivePage = Math.min(requestedPage, totalPages)
  const visibleFiles = serverPagination
    ? sorted
    : sorted.slice((effectivePage - 1) * effectivePageSize, effectivePage * effectivePageSize)
  const visibleIds = visibleFiles.map((file) => file.id)
  const allVisibleSelected = visibleIds.length > 0 && visibleIds.every((id) => selected.has(id))
  const selectedCount = selected.size
  const uploadActionDisabled = uploadDisabled || selectedCount === 0
  const selectionScopeIds = viewFiles.map((file) => file.id)
  const selectionScopeKey = selectionScopeIds.join('\u001f')
  const emptyMessage = files.length === 0
    ? emptyText
    : useCameraTabs
      ? 'No files found for this camera'
      : 'No files match the selected filters'
  const tableColumns = [
    ...(onUploadSelected ? [{ key: 'select', weight: 3 }] : []),
    { key: 'file', weight: showVideoMeta ? 27 : 38 },
    ...(showCamera ? [{ key: 'camera', weight: 8 }] : []),
    { key: 'size', weight: showVideoMeta ? 6 : 9 },
    { key: 'recorded_at', weight: showVideoMeta ? 14 : 18 },
    ...(showDeletedAt ? [{ key: 'deleted_at', weight: showVideoMeta ? 14 : 18 }] : []),
    ...(showVideoMeta ? [{ key: 'video', weight: 13 }] : []),
    { key: 'upload', weight: showVideoMeta ? 9 : 11 },
    { key: 'nas_path', weight: showVideoMeta ? 12 : 15 },
    { key: 'flags', weight: showVideoMeta ? 11 : 8 },
    ...(onDeleteLocal ? [{ key: 'actions', weight: 10 }] : []),
  ]
  const tableWeightTotal = tableColumns.reduce((sum, column) => sum + column.weight, 0)

  useEffect(() => {
    if (serverPage != null && onServerPageChange && serverPage > totalPages) {
      onServerPageChange(totalPages)
      return
    }
    setPage((current) => Math.min(current, totalPages))
  }, [onServerPageChange, serverPage, totalPages])

  useEffect(() => {
    const currentIds = new Set(selectionScopeIds)
    setSelected((prev) => {
      const next = new Set(Array.from(prev).filter((id) => currentIds.has(id)))
      return next.size === prev.size ? prev : next
    })
  }, [selectionScopeKey])

  useEffect(() => {
    if (controlledActiveCameraKey !== undefined) return
    if (!useCameraTabs) {
      setInternalActiveCameraKey(null)
      return
    }
    setInternalActiveCameraKey((current) => (
      current && cameraTabs.some((tab) => tab.key === current)
        ? current
        : cameraTabs[0]?.key ?? null
    ))
  }, [cameraTabKeys, controlledActiveCameraKey, useCameraTabs])

  useEffect(() => {
    setPage(1)
  }, [effectiveCameraKey, fromDate, toDate])

  function toggleSelect(id: string) {
    if (uploadDisabled) return
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  function selectAll() {
    if (uploadDisabled) return
    setSelected((prev) => {
      const next = new Set(prev)
      if (allVisibleSelected) {
        visibleIds.forEach((id) => next.delete(id))
      } else {
        visibleIds.forEach((id) => next.add(id))
      }
      return next
    })
  }

  function selectCamera(cameraKey: string) {
    if (controlledActiveCameraKey === undefined) {
      setInternalActiveCameraKey(cameraKey)
    }
    onCameraChange?.(cameraKey)
  }

  function toggleExpanded() {
    const next = !expanded
    if (controlledExpanded === undefined) {
      setInternalExpanded(next)
    }
    onExpandedChange?.(next)
  }

  function SortTh({ label, k }: { label: string; k: SortKey }) {
    if (serverPagination) {
      return <th style={thStyle}>{label}</th>
    }
    const active = sortKey === k
    return (
      <th
        style={{ ...thStyle, cursor: 'pointer', userSelect: 'none' }}
        onClick={() => toggleSort(k)}
      >
        {label}
        {active ? (sortDir === 'asc' ? ' ^' : ' v') : ''}
      </th>
    )
  }

  return (
    <div style={cardStyle} role="region" aria-label={title}>
      <div style={fileListHeaderStyle}>
        {collapsible ? (
          <button
            type="button"
            aria-expanded={expanded}
            aria-controls={contentId}
            onClick={toggleExpanded}
            style={collapsibleHeaderButtonStyle}
          >
            <ChevronDown
              size={18}
              aria-hidden="true"
              style={{
                flexShrink: 0,
                transform: expanded ? 'rotate(0deg)' : 'rotate(-90deg)',
              }}
            />
            <div style={fileListHeaderTextStyle}>
              <div style={cardTitleStyle}>{title} ({serverPagination?.total ?? filteredFiles.length})</div>
              {description && <div style={fileListDescriptionStyle}>{description}</div>}
            </div>
          </button>
        ) : (
          <div style={fileListHeaderTextStyle}>
            <div style={cardTitleStyle}>{title} ({serverPagination?.total ?? filteredFiles.length})</div>
            {description && <div style={fileListDescriptionStyle}>{description}</div>}
          </div>
        )}
        {!serverPagination && <div style={dateFilterStyle}>
          <label style={dateFilterLabelStyle}>
            <span>From</span>
            <input
              type="date"
              value={fromDate}
              onChange={(event) => setFromDate(event.target.value)}
              style={dateFilterInputStyle}
            />
          </label>
          <label style={dateFilterLabelStyle}>
            <span>To</span>
            <input
              type="date"
              value={toDate}
              onChange={(event) => setToDate(event.target.value)}
              style={dateFilterInputStyle}
            />
          </label>
          {(fromDate || toDate) && (
            <button type="button" onClick={() => { setFromDate(''); setToDate('') }} style={dateFilterClearStyle}>
              Clear
            </button>
          )}
        </div>}
        {onUploadSelected && (
          <button
            type="button"
            onClick={() => {
              if (uploadActionDisabled) return
              onUploadSelected(Array.from(selected))
            }}
            disabled={uploadActionDisabled}
            style={{
              ...actionBtnStyle,
              ...(uploadActionDisabled ? disabledActionBtnStyle : null),
            }}
          >
            Upload selected ({selectedCount})
          </button>
        )}
      </div>
      <div id={contentId} hidden={!expanded}>
        {useCameraTabs && (
        <div role="tablist" aria-label={`${title} camera filter`} style={cameraTabsStyle}>
          {cameraTabs.map((tab) => {
            const active = tab.key === effectiveCameraKey
            return (
              <button
                key={tab.key}
                type="button"
                role="tab"
                aria-selected={active}
                onClick={() => selectCamera(tab.key)}
                style={{
                  ...cameraTabButtonStyle,
                  ...(active ? activeCameraTabButtonStyle : null),
                }}
              >
                <span>{tab.label}</span>
                {showCameraCounts && (
                  <span
                    style={{
                      ...cameraTabCountStyle,
                      ...(active ? activeCameraTabCountStyle : null),
                    }}
                  >
                    {tab.count}
                  </span>
                )}
              </button>
            )
          })}
        </div>
      )}
      {viewFiles.length === 0 ? (
        <p style={{ color: '#aaa', fontSize: 13, margin: 0 }}>{emptyMessage}</p>
      ) : (
        <>
          <div style={{ width: '100%', overflowX: 'hidden' }}>
            <table style={tableStyle}>
              <colgroup>
                {tableColumns.map((column) => (
                  <col key={column.key} style={{ width: `${(column.weight / tableWeightTotal) * 100}%` }} />
                ))}
              </colgroup>
              <thead>
                <tr>
                  {onUploadSelected && (
                    <th style={thStyle}>
                      <input
                        type="checkbox"
                        checked={allVisibleSelected}
                        disabled={uploadDisabled}
                        onChange={selectAll}
                        aria-label="Select visible files"
                      />
                    </th>
                  )}
                  <SortTh label="File" k="file_name" />
                  {showCamera && <SortTh label="Camera" k="camera_key" />}
                  <SortTh label="Size" k="size_bytes" />
                  <SortTh label="Recorded at" k="recorded_at" />
                  {showDeletedAt && <SortTh label="Deleted at" k="deleted_at" />}
                  {showVideoMeta && <th style={thStyle}>Video</th>}
                  <SortTh label="Upload" k="upload_status" />
                  <th style={thStyle}>NAS path</th>
                  <th style={thStyle}>Flags</th>
                  {onDeleteLocal && <th style={thStyle}>Actions</th>}
                </tr>
              </thead>
              <tbody>
                {visibleFiles.map((f, i) => {
                  const flags = buildFileFlags(f)
                  return (
                    <tr
                      key={f.id}
                      title={formatRowHoverTitle(f)}
                      onClick={onUploadSelected && !uploadDisabled ? () => toggleSelect(f.id) : undefined}
                      aria-selected={selected.has(f.id)}
                      style={{
                        ...rowStyle,
                        background: selected.has(f.id) ? '#eef2ff' : i % 2 === 0 ? '#fff' : '#fafafa',
                        cursor: onUploadSelected && !uploadDisabled ? 'pointer' : 'default',
                      }}
                    >
                      {onUploadSelected && (
                        <td style={{ ...tdStyle, textAlign: 'center' }}>
                          <input
                            type="checkbox"
                            checked={selected.has(f.id)}
                            disabled={uploadDisabled}
                            aria-label={`Select ${f.file_name ?? 'file'}`}
                            onClick={(event) => event.stopPropagation()}
                            onChange={() => toggleSelect(f.id)}
                          />
                        </td>
                      )}
                      <td title={f.file_name ?? undefined} style={{ ...tdStyle, ...fileNameCellStyle }}>
                        <span style={fileNameTextStyle}>{f.file_name ?? '—'}</span>
                      </td>
                      {showCamera && (
                        <td title={f.camera_key ?? undefined} style={{ ...tdStyle, ...singleLineCellStyle }}>
                          {f.camera_key ?? '—'}
                        </td>
                      )}
                      <td style={{ ...tdStyle, ...singleLineCellStyle }}>
                        {formatBytes(f.size_bytes)}
                      </td>
                      <td style={{ ...tdStyle, ...dateCellStyle }}>
                        {formatTime(f.recorded_at)}
                      </td>
                      {showDeletedAt && (
                        <td style={{ ...tdStyle, ...dateCellStyle }}>
                          {formatTime(f.deleted_at)}
                        </td>
                      )}
                      {showVideoMeta && (
                        <td title={formatVideoHoverTitle(f)} style={{ ...tdStyle, ...singleLineCellStyle }}>
                          {formatVideoMeta(f)}
                        </td>
                      )}
                      <td style={{ ...tdStyle, ...singleLineCellStyle }}>
                        <UploadBadge status={f.upload_status} />
                      </td>
                      <td title={f.nas_path ?? undefined} style={{ ...tdStyle, ...nasPathCellStyle }}>
                        {f.nas_path ?? '—'}
                      </td>
                      <td
                        title={f.delete_failure_reason ?? f.failure_reason ?? undefined}
                        onClick={(event) => event.stopPropagation()}
                        style={{ ...tdStyle, ...flagsCellStyle }}
                      >
                        {flags.length === 0 ? (
                          <span style={emptyFlagStyle}>—</span>
                        ) : (
                          <div style={flagListStyle}>
                            {flags.map((flag) => (
                              <FlagBadge
                                key={flag.key}
                                flag={flag}
                                copiedKey={copiedKey}
                                onCopy={handleCopy}
                              />
                            ))}
                          </div>
                        )}
                      </td>
                      {onDeleteLocal && (
                        <td onClick={(event) => event.stopPropagation()} style={{ ...tdStyle, ...singleLineCellStyle }}>
                          {f.upload_status === 'uploaded'
                            && f.local_path != null
                            && f.delete_status !== 'deleted' ? <button
                            type="button"
                            onClick={() => onDeleteLocal(f)}
                            disabled={
                              deleteLocalDisabled
                              || deletingFileId !== null
                              || f.delete_status === 'deleting'
                            }
                            title={
                              deleteLocalDisabled
                                ? 'Local deletion is unavailable while an upload is active.'
                                : f.delete_status === 'deleting' || deletingFileId === f.id
                                  ? 'Local deletion is already in progress.'
                                  : deletingFileId !== null
                                    ? 'Another local deletion request is being queued.'
                                  : 'Permanently delete this uploaded file from the node.'
                            }
                            style={{
                              ...deleteButtonStyle,
                              ...(
                                deleteLocalDisabled
                                || deletingFileId !== null
                                || f.delete_status === 'deleting'
                                  ? disabledDeleteButtonStyle
                                  : null
                              ),
                            }}
                          >
                            {f.delete_status === 'deleting' || deletingFileId === f.id
                              ? 'Deleting...'
                              : f.delete_status === 'delete_failed'
                                ? 'Retry delete'
                                : 'Delete local'}
                          </button> : <span style={emptyFlagStyle}>—</span>}
                        </td>
                      )}
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
        <Pagination
          page={effectivePage}
          pageSize={effectivePageSize}
          total={paginationTotal}
          itemLabel="files"
          onPageChange={(next) => {
            if (serverPagination) serverPagination.onPageChange(next)
            else setPage(next)
          }}
        />
      </div>
    </div>
  )
}

const cardStyle: React.CSSProperties = {
  background: '#fff',
  border: '1px solid #e0e0e0',
  borderRadius: 6,
  padding: '14px 16px',
  minWidth: 0,
}

const cardTitleStyle: React.CSSProperties = {
  fontSize: 13,
  fontWeight: 700,
  color: '#333',
  textTransform: 'uppercase',
  letterSpacing: 0,
  margin: 0,
}

const fileListHeaderStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 12,
  flexWrap: 'wrap',
  minHeight: 26,
  marginBottom: 12,
}

const fileListHeaderTextStyle: React.CSSProperties = {
  minWidth: 0,
}

const collapsibleHeaderButtonStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-start',
  gap: 12,
  minWidth: 0,
  flex: 1,
  padding: 0,
  border: 0,
  background: 'transparent',
  color: 'inherit',
  cursor: 'pointer',
  font: 'inherit',
  textAlign: 'left',
}

const fileListDescriptionStyle: React.CSSProperties = {
  marginTop: 4,
  color: '#6b7280',
  fontSize: 11,
  lineHeight: 1.4,
}

const dateFilterStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  flexWrap: 'wrap',
  marginLeft: 'auto',
}

const dateFilterLabelStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 5,
  fontSize: 11,
  color: '#666',
  whiteSpace: 'nowrap',
}

const dateFilterInputStyle: React.CSSProperties = {
  width: 124,
  height: 26,
  padding: '3px 6px',
  border: '1px solid #d6dae6',
  borderRadius: 4,
  fontSize: 11,
  fontFamily: 'inherit',
  color: '#333',
  background: '#fff',
}

const dateFilterClearStyle: React.CSSProperties = {
  height: 26,
  padding: '3px 8px',
  border: '1px solid #d6dae6',
  borderRadius: 4,
  background: '#fff',
  color: '#3949ab',
  cursor: 'pointer',
  fontSize: 11,
  fontFamily: 'inherit',
  fontWeight: 600,
}

const cameraTabsStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  flexWrap: 'wrap',
  paddingBottom: 10,
  marginBottom: 10,
  borderBottom: '1px solid #f0f0f0',
}

const cameraTabButtonStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 7,
  minHeight: 26,
  padding: '4px 9px',
  border: '1px solid transparent',
  borderRadius: 4,
  background: '#f4f6fa',
  color: '#354052',
  cursor: 'pointer',
  fontSize: 12,
  fontWeight: 600,
  fontFamily: 'inherit',
  outlineOffset: 2,
}

const activeCameraTabButtonStyle: React.CSSProperties = {
  background: '#1f2d4d',
  color: '#fff',
}

const cameraTabCountStyle: React.CSSProperties = {
  minWidth: 18,
  padding: '1px 6px',
  borderRadius: 999,
  background: '#e6ebf3',
  color: '#526173',
  fontSize: 11,
  fontWeight: 700,
  textAlign: 'center',
  lineHeight: '16px',
}

const activeCameraTabCountStyle: React.CSSProperties = {
  background: 'rgba(255,255,255,0.2)',
  color: '#fff',
}

const tableStyle: React.CSSProperties = {
  width: '100%',
  tableLayout: 'fixed',
  borderCollapse: 'collapse',
  fontSize: 12,
}

const rowStyle: React.CSSProperties = {
  height: 34,
}

const thStyle: React.CSSProperties = {
  padding: '6px 8px',
  textAlign: 'left',
  fontSize: 11,
  fontWeight: 700,
  color: '#777',
  background: '#f6f7f9',
  borderBottom: '1px solid #eee',
  whiteSpace: 'nowrap',
}

const tdStyle: React.CSSProperties = {
  padding: '6px 8px',
  borderBottom: '1px solid #f0f0f0',
  verticalAlign: 'middle',
}

const singleLineCellStyle: React.CSSProperties = {
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  color: '#555',
}

const fileNameCellStyle: React.CSSProperties = {
  minWidth: 0,
  overflow: 'hidden',
  whiteSpace: 'nowrap',
}

const fileNameTextStyle: React.CSSProperties = {
  display: 'inline-block',
  width: '100%',
  color: '#111',
  fontFamily: 'monospace',
  fontSize: 11,
  fontWeight: 600,
  lineHeight: '18px',
  overflow: 'hidden',
  textOverflow: 'clip',
  whiteSpace: 'nowrap',
}

const dateCellStyle: React.CSSProperties = {
  ...singleLineCellStyle,
  color: '#666',
  fontVariantNumeric: 'tabular-nums',
}

const nasPathCellStyle: React.CSSProperties = {
  ...singleLineCellStyle,
  color: '#777',
  fontFamily: 'monospace',
  fontSize: 11,
}

const flagsCellStyle: React.CSSProperties = {
  minWidth: 0,
  overflow: 'hidden',
  whiteSpace: 'nowrap',
}

const flagListStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 4,
  minWidth: 0,
  overflow: 'hidden',
  whiteSpace: 'nowrap',
}

const flagBadgeStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  maxWidth: '100%',
  minHeight: 20,
  padding: '1px 6px',
  border: '1px solid',
  borderRadius: 10,
  fontSize: 10,
  fontFamily: 'inherit',
  fontWeight: 700,
  lineHeight: '16px',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
}

const emptyFlagStyle: React.CSSProperties = {
  color: '#bbb',
  fontSize: 12,
}

const actionBtnStyle: React.CSSProperties = {
  padding: '5px 12px',
  minWidth: 148,
  flexShrink: 0,
  background: '#3949ab',
  color: '#fff',
  border: 'none',
  borderRadius: 4,
  fontSize: 12,
  cursor: 'pointer',
  fontFamily: 'inherit',
}

const disabledActionBtnStyle: React.CSSProperties = {
  background: '#e5e7eb',
  color: '#9ca3af',
  cursor: 'not-allowed',
}

const deleteButtonStyle: React.CSSProperties = {
  padding: '4px 8px',
  border: '1px solid #ef9a9a',
  borderRadius: 4,
  background: '#fff',
  color: '#c62828',
  cursor: 'pointer',
  fontSize: 11,
  fontFamily: 'inherit',
  fontWeight: 600,
  whiteSpace: 'nowrap',
}

const disabledDeleteButtonStyle: React.CSSProperties = {
  border: '1px solid #e0e0e0',
  background: '#f8f8f8',
  color: '#aaa',
  cursor: 'not-allowed',
}
